import * as React from 'react';
import {connect} from 'react-redux';
import {IFamilyMember} from '@app-types/websocket/family.types';
import {WithStyles, withStyles,} from '@material-ui/core';
import TranslateNew from '@translate/TranslateNew';
import { generateHandoverData, paddingDays, visitationHandovers } from '../helper';
import { IDayData, IMonth } from '../calendar.types';
import { MonthviewStyle } from './monthview.style';
import Day, { handoverRows } from './Day';
import { blocksToCopy, denseChipStyle, fitLanePitch, IDayEventLayout, layoutMonthEvents, reserveOldBarDays } from './monthEventLayout';
import { memoizeLast } from '@helpers/memoizeLast';
import { ISchedule } from '@app-types/websocket/schedule.types';
import { IEvent, ILocalEvent } from '@app-types/websocket/event.types';
import { IDateRange } from '@containers/calendar/calendar.helpter';
import { getWeekNum, toIsoDate } from '@helpers/DateHelper';
import { HELP_TOPICS } from '@constants/suggestionTypes';
import * as UserActions from '@services/user/user.actions';
import { holidays as allHolidays } from '../holidays/holidays';
import { IExternalCalenderEvent } from '@app-types/api/externalCalendar.types';

const tn: TranslateNew = TranslateNew.getInstance();

interface IProps extends WithStyles<typeof MonthviewStyle> {
  importedEvents:ILocalEvent[];
  monthInView:IMonth; // the view currently in viewport (ignore other months)
  month: IMonth;
  localization: "se"|"en";
  size:"small"|"medium"|"large";
  schoolAge:number; 
  standaAloneCalendar?:boolean;
  focusDay:Date;
  selectedEditDays:IDateRange[];
  highlight:IDateRange[];
  family:IFamilyMember[];
  scheduleOne:ISchedule|undefined;
  scheduleTwo:ISchedule|undefined;
  scheduleThree:ISchedule|undefined;
  events:IEvent[];
  externalEventsPerDay:{ [day: string]: IExternalCalenderEvent []}
  isScrolling:boolean;
  isEditing:boolean;
  hideEvents?:boolean;
  userId:number;
  eventStyle?:React.CSSProperties; // from settings to indicate fontsize etc
  popMenu:(ref:HTMLDivElement|null) => void;
  setFocusDate: (year:number,month:number,date:number, position:"LEFT"|"RIGHT"|"NAN", isLongPress:boolean, originalDay?:Date) => void;
  swipeEditTransaction?: (action:"START"|"COMMIT") => void;
  swipeEdit?: (year:number,month:number,date:number, swipeDays:number, swipdeStartSide:"LEFT"|"RIGHT") => void;
  showWeekNumber:boolean;
  onlyWeeksOf?:number;
  dismissHelpNotice: (notice:string) => void;
  selectIDayInsteadOfMenuPop?: boolean;
  dismissedHelpLongclick:boolean;
  holidays:{ [key:string]:{title:string, type:"HOLIDAY"|"SPECIAL_DAY"}; };
}

interface IState {
  someValue: number;
  lanePitch: number[]; // per week row: px between its event lanes, below the normal 18 when a day has more events than fit
}

class Month extends React.Component<IProps, IState> {
  // laid out once per month and input change, not per day and render: keeps scrolling smooth on older phones
  private eventLayouts = memoizeLast(layoutMonthEvents);
  private handoverAlignment = memoizeLast(alignHandoverRows);
  private rootRef: React.RefObject<HTMLDivElement> = React.createRef();
  private measureFrame: number | null = null;
  private unmounted: boolean = false;

  constructor(props: IProps) {
    super(props);

    this.state = {
      someValue: 1,
      lanePitch: [],
    };

  }

  public componentDidMount() {
    this.measureLanes();
    window.addEventListener('resize', this.scheduleMeasure);
    // the handover text under the custody bar can wrap differently once the font has loaded
    const fonts = (document as any).fonts;
    if (fonts && fonts.ready) { fonts.ready.then(this.scheduleMeasure); }
  }

  public componentDidUpdate() {
    this.measureLanes();
  }

  public componentWillUnmount() {
    this.unmounted = true;
    window.removeEventListener('resize', this.scheduleMeasure);
    if (this.measureFrame !== null) {
      cancelAnimationFrame(this.measureFrame);
      this.measureFrame = null;
    }
  }

  public render() {
    const {classes, month, size, scheduleOne, scheduleTwo, scheduleThree, family, events, 
      isEditing, hideEvents, monthInView, importedEvents, externalEventsPerDay} = this.props;
    
    // only render this, prev and next month to save resources
    let prevMonthYear = monthInView.year;
    let prevMonth = monthInView.month-1;
    if (prevMonth === -1) {
      prevMonth = 11;
      prevMonthYear = prevMonthYear -1;
    }

    let nextMonthYear = monthInView.year;
    let nextMonth = monthInView.month+1;
    if (nextMonth === 12) {
      nextMonth = 0;
      nextMonthYear = nextMonthYear +1;
    }

    if (!(month.month === monthInView.month && month.year === monthInView.year) 
      && !(month.month === prevMonth && month.year === prevMonthYear) 
      && !(month.month === nextMonth && month.year === nextMonthYear)) {
      return (<div className={classes.month} 
        id={month.year+"-"+month.month}
        style={this.props.size==="large"?{height:"100%"}:{height:(40*(month.weeksInMonth))+10}} />);
    }

    const numDays = month.dayData.length;
    let padding:number = paddingDays(month.month,month.year,this.props.localization);
    let topDaysNoBorder:number = 7-padding;

    const isHighlight = month.dayData.map((dayData) => {
      const midDay = ((dayData.startTime + dayData.endTime + 1) / 2);
      for (const range of this.props.highlight) {
        if (midDay > range.start && midDay < range.end ) {
          return true;
        }
      }
      return false;
    });

    const eventLayouts:IDayEventLayout[]|undefined = hideEvents ? undefined
      : this.eventLayouts(month.dayData, padding, events, importedEvents, externalEventsPerDay);
    // days crossed by the same multi-day bars start their events at the same height, so each one also
    // leaves room for what the others show above their events
    let alignRows:Array<JSX.Element[][]|undefined>|undefined;
    let reserveOldBars:boolean[]|undefined;
    if (eventLayouts !== undefined && size === "large" && !this.props.standaAloneCalendar) {
      if (isEditing) {
        reserveOldBars = reserveOldBarDays(eventLayouts, isHighlight);
      } else {
        alignRows = this.handoverAlignment(month.dayData, eventLayouts, scheduleOne, scheduleTwo, scheduleThree,
          this.props.schoolAge, classes.dayHandoverLocation);
      }
    }

    let day:number = 0;
    const weeks: JSX.Element[] = [];

    let height = "40px";
    if (this.props.size === "large") {
      height = "max(calc(100% / "+month.weeksInMonth+"), 40px)";
    }

    // let spinDate:Date = new Date(month.year, month.month, 1);
    while (day < numDays) {
      const days: JSX.Element[] = [];
      while (padding > 0) {
        days.push(<div key={"p"+padding} className={classes.fakeDay} style={this.props.showWeekNumber?{width:'calc((100% / 7) - 3px)'}:{}}/>);
        padding = padding -1;
      }

      const isLastWeek = (day + 7) >= (numDays);

      const weekNum:number = getWeekNum(new Date(((month.dayData[day].startTime + month.dayData[day].endTime + 1) / 2)));

      while (days.length < 7) {
        

        if (day < numDays) {

          const noTopBorder = (topDaysNoBorder > 0);
          topDaysNoBorder = topDaysNoBorder -1;

          let isSelectedForEdit:boolean = false;
          const midDay = ((month.dayData[day].startTime + month.dayData[day].endTime + 1) / 2);
          for (const range of this.props.selectedEditDays) {
            if (midDay > range.start && midDay < range.end ) {
              isSelectedForEdit = true;
              break;
            }
          }
          const highlight:boolean = isHighlight[day];
          const isFocus:boolean = (this.props.focusDay.getDate() === month.dayData[day].date &&
            this.props.focusDay.getMonth() === month.dayData[day].month &&
            this.props.focusDay.getFullYear() === month.dayData[day].year);

          const needBottomRow:boolean = ((numDays - day) < 8);
          const needLeftBorder:boolean = (day === 0 || days.length === 0);

          days.push(<Day 
            userId={this.props.userId}
            isEditing={isEditing}
            key={day} 
            standaAloneCalendar={this.props.standaAloneCalendar}
            localization={this.props.localization} 
            size={size} 
            family={family}
            schoolAge={this.props.schoolAge}
            events={events}
            selectIDayInsteadOfMenuPop={this.props.selectIDayInsteadOfMenuPop}
            importedEvents={importedEvents}
            externalEvents={externalEventsPerDay[toIsoDate(new Date(midDay))]}
            eventLayout={eventLayouts && eventLayouts[day]}
            alignHandoverRows={alignRows && alignRows[day]}
            reserveOldBars={reserveOldBars && reserveOldBars[day]}
            data={month.dayData[day]}
            scheduleOne={scheduleOne}
            scheduleTwo={scheduleTwo}
            scheduleThree={scheduleThree}
            isSelectedForEdit={isSelectedForEdit} 
            isFocus={isFocus}
            isHighlight={highlight}
            setFocusDate={this.props.setFocusDate}
            swipeEdit={this.props.swipeEdit}
            swipeEditTransaction={this.props.swipeEditTransaction}
            hideTopBorder={noTopBorder}
            needLeftBorder={needLeftBorder}
            needBottomRow={(needBottomRow && !isLastWeek)}
            showWeekNumber={this.props.showWeekNumber}
            popMenu={this.props.popMenu}
            onlyWeeksOf={this.props.onlyWeeksOf}
            holidays={this.props.holidays}
            hasDismissedLongPress={this.props.dismissedHelpLongclick}
            dismissHelpNotice={this.props.dismissHelpNotice}
            eventStyle={this.props.eventStyle}
            laneRow={weeks.length}
            denseChipStyle={denseChipStyle(this.state.lanePitch[weeks.length])}
            hideEvents={hideEvents} />);

        } else {
          days.push(<div key={"p"+day} className={classes.fakeDay} style={this.props.showWeekNumber?{width:'calc((100% / 7) - 3px)'}:{}}/>);
        }
        day = day+1;
      }
      weeks.push(
        <div className={classes.week} key={"w"+weeks.length} style={{height}}>
          {this.props.showWeekNumber && <div className={classes.weekNumber}>{weekNum}</div>}
          {days}
        </div>
      );
    }

    return (
      <div className={classes.month} id={month.year+"-"+month.month} style={this.props.size==="large"?{height:"100%"}:{}}
        ref={this.rootRef} onTransitionEnd={this.scheduleMeasure} >
        <div className={classes.monthScrollName} style={{opacity:this.props.isScrolling?1:0}}>
          <div className={classes.monthScrollNameLabel} >{tn.lang.getMonthName(month.month)}</div>
          <div className={classes.monthScrollNameLine}/>
        </div>
        {weeks}
      </div>
    );
  }

  // Gives each week row the tallest lanes that fit the events of all its days: reads, for every day with events,
  // the room from where its lanes start to the bottom of the day. It runs after React has written the DOM, so the
  // browser lays the month out once as it would anyway, and it only sets state when a row's lanes change.
  private measureLanes = () => {
    const pitches:number[] = [];
    const root = this.rootRef.current;
    if (root !== null) {
      const starts:HTMLElement[] = Array.prototype.slice.call(root.querySelectorAll('[data-lane-row]'));
      for (const start of starts) {
        const dayElement = start.parentElement;
        if (dayElement === null) { continue; }
        const row = Number(start.getAttribute('data-lane-row'));
        const pitch = fitLanePitch(dayElement.clientHeight - start.offsetTop, Number(start.getAttribute('data-lanes')));
        pitches[row] = pitches[row] === undefined ? pitch : Math.min(pitches[row], pitch);
      }
    }
    const current = this.state.lanePitch;
    let same = current.length === pitches.length;
    for (let row = 0; same && row < pitches.length; row++) {
      same = current[row] === pitches[row];
    }
    if (!same) {
      this.setState({ lanePitch: pitches });
    }
  };

  // measures once in the next frame, however many resizes, font loads or finished transitions come in
  private scheduleMeasure = () => {
    if (this.unmounted || this.measureFrame !== null) { return; }
    this.measureFrame = requestAnimationFrame(() => {
      this.measureFrame = null;
      this.measureLanes();
    });
  };

}



// Per day: the handover rows of the other days in its align group, for the day to stack invisibly under its own
const alignHandoverRows = (days:IDayData[], layouts:IDayEventLayout[], scheduleOne:ISchedule|undefined,
  scheduleTwo:ISchedule|undefined, scheduleThree:ISchedule|undefined, schoolAge:number, className:string):Array<JSX.Element[][]|undefined> => {
  const schedules:ISchedule[] = [];
  for (const schedule of [scheduleOne, scheduleTwo, scheduleThree]) {
    if (schedule !== undefined) { schedules.push(schedule); }
  }

  const rows:JSX.Element[][] = [];
  const keys:Array<string|null> = []; // what the rows say, so days with the same rows are copied once
  for (let day = 0; day < days.length; day++) {
    rows.push([]);
    keys.push(null);
    if (layouts[day].alignGroup === -1) { continue; }
    const parts:string[] = [];
    schedules.forEach((schedule, i) => {
      const { startTime, endTime } = days[day];
      const handovers = visitationHandovers(
        generateHandoverData(schedule.data.scheduleEntriesParentA, schedule.data.parentA, startTime, endTime),
        generateHandoverData(schedule.data.scheduleEntriesParentB, schedule.data.parentB, startTime, endTime));
      if (handovers.length > 0) {
        rows[day].push(<React.Fragment key={i}>{handoverRows(handovers, schedule, schoolAge, className, false)}</React.Fragment>);
        parts.push(i + ':' + handovers.map((vis) => (vis.atSchool ? 'school' : tn.formatTime(new Date(vis.time)) + ' ' + (vis.location || '')))
          .join('|'));
      }
    });
    if (parts.length > 0) { keys[day] = parts.join(';'); }
  }

  return blocksToCopy(layouts, keys).map((others) => (others.length > 0 ? others.map((other) => rows[other]) : undefined));
};

function mapStateToProps(state: any) {
  const userId = state.user.session.data.id;
  const events = state.event.events;
  const country = (state.user.calendarSettings && state.user.calendarSettings.holidaysCountry)
    ?state.user.calendarSettings.holidaysCountry
    :undefined;
  const holidays = allHolidays.getHolidays(country);
  const dismissedHelpMessages = state.user.dismissedHelpNotices;
  let dismissedHelpLongclick = false;
  if (dismissedHelpMessages) {
    if (dismissedHelpMessages.includes(HELP_TOPICS.LONGCLICK)) {
      dismissedHelpLongclick = true;
    }
  }

  return {
    userId,
    family: state.family.family,
    events,
    holidays,
    dismissedHelpLongclick,
  };
}

const mapDispatchToProps = {
  dismissHelpNotice:UserActions.dismissHelpNotice,
  
};

export default connect(mapStateToProps,mapDispatchToProps)(withStyles(MonthviewStyle)(Month));



// WEBPACK FOOTER //
// ./src/components/calendar2/monthView/Month.tsx